import { getCurrentInstance } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { trackEvent as amplitudeTrack } from 'shared/amplitude';

/**
 * Returns a track() function that forwards every product event to BOTH
 * analytics backends:
 *   - June.so (vm.$track) — legacy SDK that existing dashboards read from.
 *     Source of truth today.
 *   - Amplitude (shared/amplitude.trackEvent) — added so we can consolidate
 *     onto Amplitude over time (session replay + heatmaps + autocapture
 *     already live there). Calls are wrapped in try/catch so a failure in
 *     one backend never starves the other.
 *
 * trackEvent already no-ops when Amplitude hasn't been initialized (ad-block,
 * init failure, opt-out), so dual-track is safe without extra guards here.
 */
export const useTrack = () => {
  const vm = getCurrentInstance();
  if (!vm) throw new Error('must be called in setup');

  const juneTrack = vm.proxy.$track;
  return (eventName, properties) => {
    try {
      juneTrack(eventName, properties);
    } catch (e) {
      // June throwing would be unexpected; don't let it take Amplitude down.
      // eslint-disable-next-line no-console
      console.warn('[analytics] june track failed', e);
    }
    try {
      amplitudeTrack(eventName, properties);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[analytics] amplitude track failed', e);
    }
  };
};

/**
 * Emits a toast message event using a global emitter.
 * @param {string} message - The message to be displayed in the toast.
 * @param {Object|null} action - Optional action descriptor. Supported shapes:
 *   - { type: 'link', to, message }              → renders a router-link
 *   - { type: 'button', message, onClick }       → renders a clickable button
 * @param {number|null} duration - Optional toast lifetime override in ms.
 *   Falls back to SnackbarContainer's default (2500ms) when null. Use a longer
 *   duration for toasts with an action so the user has time to react.
 */
export const useAlert = (message, action = null, duration = null) => {
  emitter.emit('newToastMessage', { message, action, duration });
};
